
/* eslint-disable class-methods-use-this */
import CalculateTemperature from '@/mixins/CalculateTemperature';
import GetWeatherImage from '@/mixins/GetWeatherImage';
import { Unit } from '@/store/unit/types';
import { CompassPoint, Forecast, Weather } from '@/store/weather/types';
import { Options, mixins } from 'vue-class-component';
import { mapActions, mapGetters } from 'vuex';
import BaseSpinner from './UI/BaseSpinner.vue';

interface TransformedForecasts {
  date: string;
  minTemperature: number;
  maxTemperature: number;
  weatherStateImage: string;
}

@Options({
  methods: {
    ...mapActions(['changeUnit']),
  },
  computed: {
    ...mapGetters(['unit', 'isLoading', 'forecasts', 'currentWeather']),
  },
  components: {
    BaseSpinner,
  },
})
export default class App extends mixins(CalculateTemperature, GetWeatherImage) {
  changeUnit!: (unit: Unit) => void;

  unit!: Unit;

  isLoading!: boolean;

  forecasts!: Forecast[];

  currentWeather!: Weather;

  get transformedForecasts(): TransformedForecasts[] {
    return this.forecasts.map((forecast) => ({
      date: this.formatDate(forecast.date),
      minTemperature: this.round(
        this.calculateTemperature(forecast.minTemperature),
      ),
      maxTemperature: this.round(
        this.calculateTemperature(forecast.maxTemperature),
      ),
      weatherStateImage: this.getWeatherImage(forecast.weatherStateName),
    }));
  }

  formatDate(date: Date): string {
    if (date.getDay() === new Date().getDay() + 1) {
      return 'Tomorrow';
    }
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
    }).format(date);
  }

  round(value: number) {
    return Math.round(value);
  }

  get formattedUnit(): string {
    return `°${this.unit}`;
  }

  get windSpeed(): number {
    return this.round(this.currentWeather.windSpeed);
  }

  get windDirection(): CompassPoint {
    return this.currentWeather.windDirectionCompass;
  }

  get windCompassStyle(): object {
    type Map = {
      [point in CompassPoint]: number;
    };
    const map: Map = {
      N: 0,
      NNE: 22,
      NE: 45,
      ENE: 67,
      E: 90,
      ESE: 112,
      SE: 135,
      SSE: 158,
      S: 180,
      SSW: 202,
      SW: 225,
      WSW: 247,
      W: 270,
      WNW: 293,
      NW: 315,
      NNW: 337,
    };
    return {
      transform: `rotate(${map[this.windDirection]}deg)`,
    };
  }

  get humidity(): number {
    return this.round(this.currentWeather.humidity);
  }

  get airPressure(): number {
    return this.round(this.currentWeather.airPressure);
  }

  get visibility(): number {
    return this.round(this.currentWeather.visibility);
  }
}
